import { Editor, Frame, NodeVisibilityContext } from "@nkitaa/builder-engine";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
import { resolveThemeFromWebsiteConfig } from "./lib/theme";
import {
  buildRenderablePageJson,
  fetchBootstrap,
  fetchSession,
  normalizeRuntimeSession,
  normalizeSlugFromPathname,
} from "./lib/runtimePage";
import {
  runtimeResolver,
  knownComponentNames,
} from "./runtime/components/resolver";
import { WBThemeRuntimeProvider } from "./runtime/theme/WBThemeRuntimeProvider";
import { WBLanguageRuntimeProvider } from "./runtime/lang/WBLanguageRuntimeProvider";
import type { RuntimeBootstrapResponse } from "./lib/contracts";
import { ApiError } from "./lib/api";
import {
  clearSessionExpiredNotice,
  setSession as setSessionStore,
  useSessionExpiredNotice,
} from "./lib/auth/sessionStore";
import { useSession } from "./lib/auth/useSession";
import { t } from "./runtime/locale";
import {
  WB_FIELD_CHANGED_EVENT,
  WB_COMPANION_MODAL_EVENT,
  evaluateVisibilityFilter,
  LiveGuestValuesContext,
  type ParentFilter,
} from "./runtime/components/shared";
import {
  clearPendingScroll,
  peekPendingScroll,
} from "./runtime/lib/linkTarget";
import { LegalLineView } from "./runtime/components/legalLine";
import { RuntimeChrome } from "./runtime/consent/RuntimeChrome";
import { SlottingOverlay } from "./runtime/components/slottingOverlay";
import { applyHeadMeta, resetHeadMeta } from "./lib/headMeta";

// Capture email-link credentials at module load — BEFORE React mounts any
// component. Earlier this lived in a `useEffect` inside `App`, but React
// runs child effects before parent effects on first commit, so `auth.tsx`
// would clear `_auth_prefill` (under `ruleLoginBlocked`) before `App`'s
// effect had a chance to write it. Running synchronously at module load
// guarantees sessionStorage is populated before any component's effects
// fire, so the receiver's clear in `auth.tsx` actually catches the stash.
(function captureEmailLinkCredentials() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const combined = params.get("credentials");
  const c1 = params.get("credential1");
  const c2 = params.get("credential2");
  if (!combined && !c1 && !c2) return;

  let username: string | null = null;
  let code: string | null = null;
  if (combined) {
    try {
      const decoded = decodeBase64Loose(combined);
      const i = decoded.indexOf(":");
      if (i >= 0) {
        username = decoded.slice(0, i);
        code = decoded.slice(i + 1);
      }
    } catch {
      /* malformed */
    }
  }
  if (c1) {
    try {
      username = decodeBase64Loose(c1);
    } catch {
      /* ignore */
    }
  }
  if (c2) {
    try {
      code = decodeBase64Loose(c2);
    } catch {
      /* ignore */
    }
  }
  if (username !== null || code !== null) {
    sessionStorage.setItem(
      "_auth_prefill",
      JSON.stringify({ username: username ?? "", code: code ?? "" }),
    );
  }
  window.history.replaceState(
    null,
    "",
    window.location.pathname + window.location.hash,
  );
})();

// Decode a credential param that may be either standard base64 (legacy
// templates saved before the base64url migration) or base64url (new
// emails minted by the `{{credentialsQuery:…}}` sentinel in main-consumer).
// `URLSearchParams.get` already URL-decoded the param, which turned any
// `+` from standard base64 into a literal space. Restoring the space → `+`
// recovers the legacy form; replacing `-`/`_` recovers the base64url form.
function decodeBase64Loose(raw: string): string {
  const normalised = raw
    .replace(/ /g, "+")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const pad = normalised.length % 4;
  const padded =
    pad === 0 ? normalised : normalised + "=".repeat(4 - pad);
  return atob(padded);
}

function RuntimePage() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<
    { message: string; status: number | undefined } | undefined
  >(undefined);
  const [bootstrap, setBootstrap] = useState<RuntimeBootstrapResponse | null>(
    null,
  );
  const session = useSession();
  // Audit 2026-09-23 (4a): store-backed so the remount below cannot lose it.
  const sessionExpired = useSessionExpiredNotice();
  const [frameData, setFrameData] = useState<string>("{}");
  const [authRevision, setAuthRevision] = useState(0);
  const [liveFieldValues, setLiveFieldValues] = useState<
    Record<string, string>
  >({});
  // P9-F2: live values typed inside the companion edit modal — overlaid on
  // the main values ONLY while the modal is open (the modal is the sole
  // interactable surface then; documented v1 limitation: the overlay is
  // global, not per-node).
  const [companionOverlayValues, setCompanionOverlayValues] = useState<
    Record<string, string>
  >({});
  const [companionModalOpen, setCompanionModalOpen] = useState(false);

  useEffect(() => {
    // Debounce coalesces the post-login burst (notifyAuthChanged + the
    // matching navigate() both fire inside ~ms) so the [pathname,
    // authRevision] effect only refetches once. 50ms is below human
    // perception so login feel doesn't change.
    let timer: ReturnType<typeof setTimeout> | null = null;
    function onAuthChanged(e: Event) {
      // Optional chaining is load-bearing: `notifyAuthChanged()` (shared.tsx)
      // dispatches WITHOUT detail on login; only `clearSession` carries one.
      const reason = (e as CustomEvent<{ reason?: string } | undefined>)
        .detail?.reason;
      Sentry.addBreadcrumb({
        category: "session",
        level: "info",
        message: "auth changed",
        data: { reason: reason ?? "login" },
      });
      // Synchronous: drop any in-flight form-field values so the
      // post-logout render of `nodeVisibilityFn` can't evaluate filters
      // against the prior user's typed-in data.
      setLiveFieldValues({});
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        setAuthRevision((value) => value + 1);
      }, 50);
    }

    window.addEventListener("wb:auth-changed", onAuthChanged);
    return () => {
      window.removeEventListener("wb:auth-changed", onAuthChanged);
      if (timer !== null) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    // Well-known aliases for the acceptance/status field so visibility
    // filters match regardless of which name the backend field uses.
    const STATUS_ALIASES = ["state", "acceptanceState", "status"];

    function onFieldChanged(e: Event) {
      const { field, value, companion } = (e as CustomEvent).detail as {
        field: string;
        value: string;
        companion?: boolean;
        companionIndex?: string;
      };
      // P9-F2: companion-modal inputs feed a SEPARATE overlay — a plus-one's
      // answers must not flip rules on the main form (and vice versa).
      const setMap = companion ? setCompanionOverlayValues : setLiveFieldValues;
      setMap((prev) => {
        const next = { ...prev, [field]: value };
        // If this is a status-related field, store under all aliases
        if (STATUS_ALIASES.includes(field)) {
          for (const alias of STATUS_ALIASES) {
            next[alias] = value;
          }
        }
        return next;
      });
    }

    function onCompanionModal(e: Event) {
      const { open } = (e as CustomEvent).detail as { open: boolean };
      setCompanionModalOpen(open);
      // The overlay dies with the modal — the next open starts clean (v1:
      // one overlay, scoped by modal lifetime, not per companion index).
      if (!open) setCompanionOverlayValues({});
    }

    window.addEventListener(WB_FIELD_CHANGED_EVENT, onFieldChanged);
    window.addEventListener(WB_COMPANION_MODAL_EVENT, onCompanionModal);
    return () => {
      window.removeEventListener(WB_FIELD_CHANGED_EVENT, onFieldChanged);
      window.removeEventListener(WB_COMPANION_MODAL_EVENT, onCompanionModal);
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function load() {
      const slug = normalizeSlugFromPathname(location.pathname);

      setLoading(true);
      setError(undefined);

      try {
        // Bootstrap v1.1.7+ bundles the session payload, so the SPA's
        // first paint normally does one request. We still fire
        // /session in parallel as a fallback for older backends that
        // don't yet emit `bootstrap.session` — when the bundle is
        // present we ignore the parallel fetch's result. /session is
        // also kept around for the post-login refresh path in
        // auth.tsx which calls fetchSession directly.
        const [bootstrapResult, sessionResult] = await Promise.allSettled([
          fetchBootstrap(slug),
          fetchSession(),
        ]);

        if (bootstrapResult.status === "rejected") {
          throw bootstrapResult.reason;
        }
        const bootstrapData = bootstrapResult.value;
        const sessionData =
          bootstrapData.session !== undefined
            ? normalizeRuntimeSession(bootstrapData.session)
            : sessionResult.status === "fulfilled"
              ? sessionResult.value
              : null;
        if (
          bootstrapData.session === undefined &&
          sessionResult.status === "rejected"
        ) {
          Sentry.addBreadcrumb({
            category: "session",
            level: "warning",
            message: "fetchSession failed; rendering as anonymous",
            data: {
              error:
                sessionResult.reason instanceof Error
                  ? sessionResult.reason.message
                  : String(sessionResult.reason),
            },
          });
        }

        // A fresh session arrived ⇒ whatever expiry notice was pending is
        // stale (the guest logged back in, or a bundled session came with
        // the bootstrap). Audit 2026-09-23 (4a).
        if (sessionData !== null) clearSessionExpiredNotice();

        if (!bootstrapData.page) {
          // No page matched this route (e.g. a deep route on an event whose
          // builder site has no such page). Surface a clean not-found state
          // instead of crashing inside buildRenderablePageJson.
          if (!active) return;
          setBootstrap(bootstrapData);
          setSessionStore(sessionData);
          setError({ message: "Page not found", status: 404 });
          return;
        }

        const parsed = await buildRenderablePageJson(
          slug,
          bootstrapData,
          knownComponentNames,
        );

        if (!active) return;

        setBootstrap(bootstrapData);
        // Re-audit 2026-09-23 (F-6): a restored (non-null) session opens the
        // 10 s lap window in `api.ts` — a slotting 401 inside it is not an
        // expiry. The store records the mark itself (fix round 3, H1).
        setSessionStore(sessionData);
        setFrameData(parsed);
      } catch (err) {
        if (!active) return;
        const status = err instanceof ApiError ? err.status : undefined;
        // On 401 from bootstrap (forward-compat — current backend wraps
        // failures as 503, never 401), drop any cached session so the
        // error UI doesn't render against a previous user's identity.
        if (status === 401) setSessionStore(null);
        setError({
          message: err instanceof Error ? err.message : "Failed to load page.",
          status,
        });
      } finally {
        if (active) setLoading(false);
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [location.pathname, authRevision]);

  // Scroll to a section anchor once the page's frame has rendered: a pending
  // jump set by a cross-page nav link, or a hash on a direct page load. Same-page
  // jumps are scrolled inline by navigateOrScroll without navigating, so they
  // don't reach here. Retry briefly since the section mounts a tick after data.
  useEffect(() => {
    if (loading) return;
    // L2: PEEK. This effect also runs while the OLD page is still mounted
    // (the pathname changes before `loading` flips), and consuming there is
    // what dropped the jump — that run cannot find the element and is then
    // cancelled by the next render's cleanup.
    const pending = peekPendingScroll();
    const id =
      pending ??
      (location.hash.length > 1
        ? decodeURIComponent(location.hash.slice(1))
        : null);
    if (!id) return;
    let cancelled = false;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Only NOW is the jump spent.
        clearPendingScroll();
        return;
      }
      if (attempts++ < 20) {
        setTimeout(tryScroll, 50);
      } else {
        // Gave up: the target page really has no such section. Drop it so a
        // stale id cannot hijack the next navigation. A CANCELLED run never
        // reaches here, which is the whole point — it hands the id on.
        clearPendingScroll();
      }
    };
    const timer = setTimeout(tryScroll, 50);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [frameData, location.pathname, location.hash, loading]);

  useEffect(() => {
    // Title + description + OG + robots in one place (per-page SEO, B-6).
    applyHeadMeta(
      (bootstrap?.page as Parameters<typeof applyHeadMeta>[0]) ?? null,
      (bootstrap?.websiteConfig as Parameters<typeof applyHeadMeta>[1]) ?? null,
    );
    return () => {
      resetHeadMeta();
    };
  }, [bootstrap]);

  const hasBootstrap = bootstrap !== null;

  const themeConfig = useMemo(() => {
    if (!bootstrap) return null;
    return resolveThemeFromWebsiteConfig(bootstrap.websiteConfig);
  }, [bootstrap]);

  // Merge session data with live form field values (live values take
  // priority); while the companion modal is open, the companion's own typed
  // values overlay both (P9-F2). Shared by the node-visibility filter and the
  // field-definition condition context provided to form inputs.
  const liveGuestValues = useMemo<Record<string, unknown>>(
    () => ({
      ...(session?.user ?? {}),
      ...liveFieldValues,
      ...(companionModalOpen ? companionOverlayValues : {}),
    }),
    [session, liveFieldValues, companionModalOpen, companionOverlayValues],
  );

  const nodeVisibilityFn = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: Record<string, any>) => {
      // Boolean visibility check
      if (props.isVisible === false) return false;

      // Rule-based visibility filter
      const filter = props.visibilityFilter as ParentFilter | undefined;
      if (!filter) return true;

      return evaluateVisibilityFilter(filter, liveGuestValues);
    },
    [liveGuestValues],
  );

  if (loading) {
    return (
      <div className="runtime-status">
        <div className="relative w-5 h-5">
          <div
            className="absolute inset-0 border-2 rounded-full"
            style={{ borderColor: "#ddd" }}
          />
          <div
            className="absolute inset-0 border-2 border-transparent rounded-full animate-spin"
            style={{ borderTopColor: "#121212" }}
          />
        </div>
      </div>
    );
  }

  if (error?.status === 404) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-dvh px-8 py-12"
        style={{
          fontFamily: "Inter, system-ui, sans-serif",
          background: "#ffffff",
        }}
      >
        <div className="flex flex-col items-center gap-6 text-center flex-1 justify-center">
          <img
            src="/assets/favicon-180x180.png"
            alt="Veltric"
            width="56"
            height="56"
          />
          <div className="flex flex-col gap-2">
            <h1
              className="text-7xl font-bold tracking-tight"
              style={{ color: "#121212" }}
            >
              404
            </h1>
            <p className="text-base" style={{ color: "#5d5d5d" }}>
              The page you're looking for doesn't exist.
            </p>
          </div>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium transition-colors"
            style={{ backgroundColor: "#222222", color: "#ffffff" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            Back to Home
          </a>
        </div>
        <footer className="pt-8 text-xs" style={{ color: "#ABADB7" }}>
          <a
            href="https://veltric.app"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors"
            style={{ color: "#ABADB7" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#5d5d5d";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#ABADB7";
            }}
          >
            Veltric Guest Solutions
          </a>
        </footer>
        {/* No bootstrap here ⇒ no site language and no operator URLs: the
            browser's language, the platform's pages. */}
        <LegalLineView lang={navigator.language} primaryLanguage={null} />
      </div>
    );
  }

  if (error || !hasBootstrap || !themeConfig) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-dvh px-8 py-12"
        style={{
          fontFamily: "Inter, system-ui, sans-serif",
          background: "#ffffff",
        }}
      >
        <div className="flex flex-col items-center gap-6 text-center flex-1 justify-center">
          <img
            src="/assets/favicon-180x180.png"
            alt="Veltric"
            width="56"
            height="56"
          />
          <p className="text-base" style={{ color: "#5d5d5d" }}>
            {error?.message ?? "No page data."}
          </p>
        </div>
        <footer className="pt-8 text-xs" style={{ color: "#ABADB7" }}>
          <a
            href="https://veltric.app"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors"
            style={{ color: "#ABADB7" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#5d5d5d";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#ABADB7";
            }}
          >
            Veltric Guest Solutions
          </a>
        </footer>
        {/* No bootstrap here ⇒ no site language and no operator URLs: the
            browser's language, the platform's pages. */}
        <LegalLineView lang={navigator.language} primaryLanguage={null} />
      </div>
    );
  }

  return (
    <WBThemeRuntimeProvider
      websiteConfig={bootstrap.websiteConfig}
      fields={bootstrap.fields}
      session={session}
      {...(bootstrap.registrationType
        ? { registrationType: bootstrap.registrationType }
        : {})}
      hasWhiteLabelAccess={bootstrap.hasWhiteLabelAccess ?? false}
      {...(bootstrap.event?.timeZone
        ? { eventTimezone: bootstrap.event.timeZone }
        : {})}
    >
      {/* Only here, in the final branch: the loading / error branches have
          no page to put it on, and `t()` is module-global so this works
          outside the language provider. Audit 2026-09-23 (4a). */}
      {sessionExpired ? (
        <div
          role="alert"
          data-testid="wb-session-expired"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            padding: "0.75rem 1rem",
            backgroundColor: "#fef3c7",
            color: "#92400e",
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: "0.875rem",
          }}
        >
          <span>{t("sessionExpired")}</span>
          <button
            type="button"
            onClick={clearSessionExpiredNotice}
            aria-label={t("close")}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              fontSize: "1rem",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      ) : null}
      <WBLanguageRuntimeProvider
        primaryLanguage={bootstrap.websiteConfig.primaryLanguage}
        languages={bootstrap.websiteConfig.languages}
        guestLanguage={session?.language as string | undefined}
      >
        {/* Consent provider + legal line + language switcher + banner — one
            component, so the tests mount the very tree production mounts. */}
        <RuntimeChrome websiteConfig={bootstrap.websiteConfig}>
          <NodeVisibilityContext.Provider value={nodeVisibilityFn}>
            <LiveGuestValuesContext.Provider value={liveGuestValues}>
              <Editor enabled={false} resolver={runtimeResolver}>
                <Frame data={frameData} />
              </Editor>
            </LiveGuestValuesContext.Provider>
          </NodeVisibilityContext.Provider>
        </RuntimeChrome>
      </WBLanguageRuntimeProvider>
      <SlottingOverlay />
    </WBThemeRuntimeProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SentryRoutes>
        <Route path="/" element={<RuntimePage />} />
        <Route path="/register" element={<RuntimePage />} />
        <Route path="/login" element={<RuntimePage />} />
        <Route path="/contact" element={<RuntimePage />} />
        <Route path="*" element={<RuntimePage />} />
      </SentryRoutes>
    </BrowserRouter>
  );
}
